import { useTranslate } from '@refinedev/core';
import { App as AntdApp, Input, InputNumber, Select, Space, Typography } from 'antd';
import { useState } from 'react';
import { SectionDrawer, fmtDay, type ToolLocale } from '../../components/ToolSection';
import { useRefreshClientPage } from '../clients/refreshClientPage';
import {
  ROSE_NEEDS,
  chooseInput,
  chooseOk,
  describeInput,
  describeOk,
  emptyChoose,
  emptyDescribe,
  emptyRate,
  meanText,
  newId,
  rateInput,
  rateOk,
  type ChooseDraft,
  type DescribeDraft,
  type RateDraft,
  type RoseStrategy,
} from './roseModel';
import { useValuesCommand } from './useValuesCommand';

/**
 * E53 · the three in-session acts as side drawers (design PORTAL-SECTIONS-2026-10-22 §2.4 (d)2–4): describe, rate and choose a way WITH
 * the client. The same commands as before (performer = the therapist, the practice's record, no homework completed); Save is never
 * disabled — a missing field is a lasting sentence in the drawer. Each is mounted only while open, so it always starts from the form's
 * defaults.
 */
const INSPIRED = ['myPast', 'someoneClose', 'someoneFar', 'bookOrFilm', 'valuesList', 'other'];

type CommonProps = {
  clientId: string;
  clientName: string;
  onClose: () => void;
  onSaved: () => void;
};
type T = (k: string, o?: Record<string, unknown>) => string;

const useSaver = (onClose: () => void, onSaved: () => void) => {
  const t = useTranslate() as unknown as T;
  const { message } = AntdApp.useApp();
  const refreshClientPage = useRefreshClientPage();
  const [error, setError] = useState<string | null>(null);
  const done = () => {
    message.success(t('rose.hub.session.saved'));
    refreshClientPage('assign');
    onClose();
    onSaved();
  };
  return { t, error, setError, done };
};

// ── describe ──────────────────────────────────────────────────────────────────────────────────

export const DescribeWayDrawer = ({ clientId, clientName, onClose, onSaved }: CommonProps) => {
  const { run, isLoading } = useValuesCommand();
  const { t, error, setError, done } = useSaver(onClose, onSaved);
  const [draft, setDraft] = useState<DescribeDraft>(emptyDescribe());
  const patch = (p: Partial<DescribeDraft>) => {
    setError(null);
    setDraft((d) => ({ ...d, ...p }));
  };
  const onSave = async () => {
    if (!describeOk(draft)) {
      setError(t('rose.hub.session.describeRequired'));
      return;
    }
    const res = await run(
      'describeBehaviorStrategyInSession',
      describeInput(clientId, newId(), draft),
      t('rose.hub.session.error'),
    );
    if (res.ok) done();
    else setError(res.message);
  };
  return (
    <SectionDrawer
      open
      title={`${t('rose.hub.session.describe')} — ${clientName}`}
      testId="rose-hub-describe"
      saveTestId="rose-hub-describe-save"
      context={<Typography.Text type="secondary">{t('rose.hub.session.intro')}</Typography.Text>}
      saveLabel={t('rose.hub.session.save')}
      saving={isLoading}
      error={error}
      dirty={JSON.stringify(draft) !== JSON.stringify(emptyDescribe())}
      onSave={() => {
        onSave().catch(() => undefined);
      }}
      onClose={onClose}
    >
      <Space direction="vertical" style={{ width: '100%' }}>
        <Input
          addonBefore={t('rose.hub.session.name')}
          data-testid="rose-hub-describe-name"
          maxLength={200}
          onChange={(e) => patch({ name: e.target.value })}
          value={draft.name}
        />
        <Select
          data-testid="rose-hub-describe-kind"
          onChange={(kind) => patch({ kind })}
          options={[
            { value: 'current', label: t('rose.hub.kindCurrent') },
            { value: 'alternative', label: t('rose.hub.kindAlternative') },
          ]}
          style={{ width: '100%' }}
          value={draft.kind}
        />
        <Input.TextArea
          autoSize={{ minRows: 3, maxRows: 8 }}
          data-testid="rose-hub-describe-behaviors"
          onChange={(e) => patch({ behaviors: e.target.value })}
          placeholder={t('rose.hub.session.behaviors')}
          value={draft.behaviors}
        />
        <Select
          allowClear
          data-testid="rose-hub-describe-inspired"
          onChange={(inspiredKind) =>
            patch({ inspiredKind: inspiredKind ?? null, inspiredRef: inspiredKind ? draft.inspiredRef : '' })
          }
          options={INSPIRED.map((k) => ({ value: k, label: t(`rose.describe.inspiredKind.${k}`) }))}
          placeholder={t('rose.describe.inspired')}
          style={{ width: '100%' }}
          value={draft.inspiredKind ?? undefined}
        />
        {draft.inspiredKind ? (
          <Input
            addonBefore={t('rose.hub.session.inspiredRef')}
            data-testid="rose-hub-describe-inspired-ref"
            maxLength={200}
            onChange={(e) => patch({ inspiredRef: e.target.value })}
            value={draft.inspiredRef}
          />
        ) : null}
      </Space>
    </SectionDrawer>
  );
};

// ── rate ───────────────────────────────────────────────────────────────────────────────────────

const basisLabel = (t: T, basis: string | null) =>
  basis ? t(`rose.hub.history.basis${basis === 'lived' ? 'Lived' : 'Imagined'}`) : '';

export const RateWayDrawer = ({
  clientId,
  clientName,
  ways,
  locale,
  onClose,
  onSaved,
}: CommonProps & { ways: RoseStrategy[]; locale: ToolLocale }) => {
  const { run, isLoading } = useValuesCommand();
  const { t, error, setError, done } = useSaver(onClose, onSaved);
  const first = (ways.find((w) => !w.scores) ?? ways[0])?.strategyId ?? null;
  const opening: RateDraft = { ...emptyRate(), strategyId: first };
  const [draft, setDraft] = useState<RateDraft>(opening);
  const patch = (p: Partial<RateDraft>) => {
    setError(null);
    setDraft((d) => ({ ...d, ...p }));
  };
  const way = ways.find((w) => w.strategyId === draft.strategyId);
  const last = way?.scores
    ? t('rose.hub.session.lastRating', {
        mean: meanText(way.cappedMean),
        basis: basisLabel(t, way.basis),
        date: way.ratedAt ? fmtDay(way.ratedAt, locale) : '',
      })
    : null;

  const onSave = async () => {
    if (!rateOk(draft)) {
      setError(t('rose.hub.session.rateRequired'));
      return;
    }
    const res = await run(
      'rateStrategyOnNeedsInSession',
      rateInput(clientId, draft),
      t('rose.hub.session.error'),
    );
    if (res.ok) done();
    else setError(res.message);
  };

  return (
    <SectionDrawer
      open
      title={`${t('rose.hub.session.rate')} — ${clientName}`}
      testId="rose-hub-rate"
      saveTestId="rose-hub-rate-save"
      width="wide"
      context={
        last ? (
          <Typography.Text type="secondary" data-testid="rose-hub-rate-last">
            {last}
          </Typography.Text>
        ) : null
      }
      saveLabel={t('rose.hub.session.rateSave')}
      saving={isLoading}
      error={error}
      dirty={JSON.stringify(draft) !== JSON.stringify(opening)}
      onSave={() => {
        onSave().catch(() => undefined);
      }}
      onClose={onClose}
    >
      <Space direction="vertical" style={{ width: '100%' }}>
        <Space wrap>
          <Select
            data-testid="rose-hub-rate-way"
            onChange={(strategyId) => patch({ strategyId })}
            options={ways.map((w) => ({ value: w.strategyId, label: w.name }))}
            style={{ width: 280 }}
            value={draft.strategyId ?? undefined}
          />
          <Select
            data-testid="rose-hub-rate-basis"
            onChange={(basis) => patch({ basis })}
            options={[
              { value: 'lived', label: t('rose.hub.history.basisLived') },
              { value: 'imagined', label: t('rose.hub.history.basisImagined') },
            ]}
            style={{ width: 160 }}
            value={draft.basis}
          />
        </Space>
        {ROSE_NEEDS.map((need, i) => {
          const lastScore = way?.scores?.[need];
          return (
            <Space key={need} wrap>
              <span style={{ width: 280, display: 'inline-block' }}>
                {i + 1}. {t(`rose.needs.${need}`)}
              </span>
              <InputNumber
                data-testid={`rose-hub-rate-score-${need}`}
                max={10}
                min={1}
                onChange={(v) =>
                  patch({ scores: { ...draft.scores, [need]: v === null ? null : Math.round(Number(v)) } })
                }
                precision={0}
                value={draft.scores[need] ?? null}
              />
              <Input
                data-testid={`rose-hub-rate-example-${need}`}
                maxLength={500}
                onChange={(e) => patch({ examples: { ...draft.examples, [need]: e.target.value } })}
                style={{ width: 240 }}
                value={draft.examples[need] ?? ''}
              />
              {lastScore !== undefined && lastScore !== null ? (
                <Typography.Text type="secondary" data-testid={`rose-hub-rate-lastscore-${need}`}>
                  {t('rose.hub.session.lastScore', { n: lastScore })}
                </Typography.Text>
              ) : null}
            </Space>
          );
        })}
      </Space>
    </SectionDrawer>
  );
};

// ── choose ─────────────────────────────────────────────────────────────────────────────────────

export const ChooseWayDrawer = ({
  clientId,
  clientName,
  rated,
  onClose,
  onSaved,
}: CommonProps & { rated: RoseStrategy[] }) => {
  const { run, isLoading } = useValuesCommand();
  const { t, error, setError, done } = useSaver(onClose, onSaved);
  const only = rated.length === 1 ? rated[0] : null;
  const opening: ChooseDraft = {
    ...emptyChoose(),
    strategyId: only?.strategyId ?? null,
    value: only?.name ?? '',
  };
  const [draft, setDraft] = useState<ChooseDraft>(opening);
  const patch = (p: Partial<ChooseDraft>) => {
    setError(null);
    setDraft((d) => ({ ...d, ...p }));
  };
  const onSave = async () => {
    if (!chooseOk(draft)) {
      setError(t('rose.hub.session.chooseRequired'));
      return;
    }
    const res = await run(
      'chooseBehaviorStrategyInSession',
      chooseInput(clientId, draft, rated.map((w) => w.strategyId)),
      t('rose.hub.session.error'),
    );
    if (res.ok) done();
    else setError(res.message);
  };
  return (
    <SectionDrawer
      open
      title={`${t('rose.hub.session.choose')} — ${clientName}`}
      testId="rose-hub-choose"
      saveTestId="rose-hub-choose-save"
      saveLabel={t('rose.hub.session.chooseSave')}
      saving={isLoading}
      error={error}
      dirty={JSON.stringify(draft) !== JSON.stringify(opening)}
      onSave={() => {
        onSave().catch(() => undefined);
      }}
      onClose={onClose}
    >
      <Space direction="vertical" style={{ width: '100%' }}>
        <Select
          data-testid="rose-hub-choose-way"
          onChange={(strategyId) =>
            patch({
              strategyId,
              value: draft.value.trim() ? draft.value : (rated.find((w) => w.strategyId === strategyId)?.name ?? ''),
            })
          }
          options={rated.map((w) => ({
            value: w.strategyId,
            label: `${w.name} · ${meanText(w.cappedMean)}`,
          }))}
          placeholder={t('rose.hub.session.way')}
          style={{ width: '100%' }}
          value={draft.strategyId ?? undefined}
        />
        <Input
          addonBefore={t('rose.hub.session.value')}
          data-testid="rose-hub-choose-value"
          maxLength={200}
          onChange={(e) => patch({ value: e.target.value })}
          value={draft.value}
        />
        <Input.TextArea
          autoSize={{ minRows: 2, maxRows: 6 }}
          data-testid="rose-hub-choose-beliefs"
          onChange={(e) => patch({ beliefs: e.target.value })}
          placeholder={t('rose.hub.session.beliefs')}
          value={draft.beliefs}
        />
        <Input.TextArea
          autoSize={{ minRows: 2, maxRows: 6 }}
          data-testid="rose-hub-choose-shared"
          onChange={(e) => patch({ sharedBy: e.target.value })}
          placeholder={t('rose.hub.session.sharedBy')}
          value={draft.sharedBy}
        />
      </Space>
    </SectionDrawer>
  );
};
